import { useEffect, useState } from 'react';
import { Container, Paper, SimpleGrid, Stack, Text, Title, Group, Progress } from '@mantine/core';
import { pb } from '../../lib/pocketbase';

interface StatsData {
  topControlPoints: Array<{
    name: string;
    count: number;
  }>;
  monthlyAudits: Array<{
    month: string;
    count: number;
  }>;
  departmentMeasures: Array<{
    department: string;
    measures: number;
  }>;
}

const getMonthName = (date: Date): string => {
  return date.toLocaleString('de-DE', { month: 'long', year: 'numeric' });
};

export function Statistics() {
  const [stats, setStats] = useState<StatsData>({
    topControlPoints: [],
    monthlyAudits: [],
    departmentMeasures: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const audits = await pb.collection('audits').getFullList({
          expand: 'dienststelle.direktion,kontrollpunkt',
        });

        // Manually fetch auditors (users)
        // Initialize stats
        const controlPointsCount: { [key: string]: number } = {};
        const monthlyCount: { [key: string]: number } = {};
        const departmentCount: { [key: string]: number } = {};
        // const userMap = new Map<string, any>(); // Unused for now
        // Process other statistics
        audits.forEach(audit => {
          const monthYear = getMonthName(new Date(audit.auditdatum));
          monthlyCount[monthYear] = (monthlyCount[monthYear] || 0) + 1;

          if (audit.expand?.kontrollpunkt) {
            (Array.isArray(audit.expand.kontrollpunkt) ? audit.expand.kontrollpunkt : [audit.expand.kontrollpunkt])
              .forEach((punkt: any) => {
                if (punkt.sicherheitssystem) {
                  controlPointsCount[punkt.sicherheitssystem] = (controlPointsCount[punkt.sicherheitssystem] || 0) + 1;
                }
              });
          }

          if (audit.expand?.dienststelle?.name) {
            departmentCount[audit.expand.dienststelle.name] = (departmentCount[audit.expand.dienststelle.name] || 0) + 1;
          }

          // Auditor-bezogene Auswertung entfernt
        });

        // Prepare final stats
        const topControlPoints = Object.entries(controlPointsCount)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([name, count]) => ({ name, count }));

        const monthlyAudits = Object.entries(monthlyCount)
          .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
          .slice(-6)
          .map(([month, count]) => ({ month, count }));

        const departmentMeasures = Object.entries(departmentCount)
          .sort(([, a], [, b]) => b - a)
          .map(([department, measures]) => ({ department, measures }));

        setStats({
          topControlPoints,
          monthlyAudits,
          departmentMeasures,
        });
      } catch (error) {
        console.error('Error fetching statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <Text>Lade Statistiken...</Text>;
  }

  const maxMonthlyCount = stats.monthlyAudits.reduce((max, entry) => Math.max(max, entry.count), 0);
  const maxDepartmentMeasures = stats.departmentMeasures.reduce((max, entry) => Math.max(max, entry.measures), 0);
  const topControlReference = stats.topControlPoints[0]?.count ?? 0;

  return (
    <Container size="xl" py="xl">
      <Title order={2} mb="xl">Statistiken</Title>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" mt="xl">
        {/* Top Control Points */}
        <Paper shadow="sm" p="md" withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500} ta="center">Top 5 Kontrollpunkte</Text>
            {stats.topControlPoints.length > 0 ? (
              stats.topControlPoints.map(({ name, count }) => (
                <Stack key={name} gap={4}>
                  <Group justify="space-between">
                    <Text size="xs" lineClamp={1}>{name}</Text>
                    <Text size="xs" c="dimmed">{count}</Text>
                  </Group>
                  <Progress
                    value={topControlReference ? (count / topControlReference) * 100 : 0}
                    color="grape"
                    size="sm"
                  />
                </Stack>
              ))
            ) : (
              <Text size="xs" ta="center" c="dimmed">Keine Daten verfügbar.</Text>
            )}
          </Stack>
        </Paper>

        {/* Monthly Audit Trend */}
        <Paper shadow="sm" p="md" withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500} ta="center">Audits pro Monat</Text>
            {stats.monthlyAudits.length > 0 ? (
              stats.monthlyAudits.map(({ month, count }) => (
                <Stack key={month} gap={4}>
                  <Group justify="space-between">
                    <Text size="xs">{month}</Text>
                    <Text size="xs" c="dimmed">{count}</Text>
                  </Group>
                  <Progress
                    value={maxMonthlyCount ? (count / maxMonthlyCount) * 100 : 0}
                    color="cyan"
                    size="sm"
                  />
                </Stack>
              ))
            ) : (
              <Text size="xs" ta="center" c="dimmed">Keine Auditdaten verfügbar.</Text>
            )}
          </Stack>
        </Paper>

        {/* Department Measures Comparison */}
        <Paper shadow="sm" p="md" withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500} ta="center">Massnahmen nach Dienststelle</Text>
            {stats.departmentMeasures.length > 0 ? (
              stats.departmentMeasures.slice(0, 5).map(({ department, measures }) => (
                <Stack key={department} gap={4}>
                  <Group justify="space-between">
                    <Text size="xs" lineClamp={1}>{department}</Text>
                    <Text size="xs" c="dimmed">{measures}</Text>
                  </Group>
                  <Progress
                    value={maxDepartmentMeasures ? (measures / maxDepartmentMeasures) * 100 : 0}
                    color="indigo"
                    size="sm"
                  />
                </Stack>
              ))
            ) : (
              <Text size="xs" ta="center" c="dimmed">Keine Daten zu Dienststellen vorhanden.</Text>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>
    </Container>
  );
}
